/* eslint-disable */
import * as React from "react";
import { WithStyles, withStyles } from "@material-ui/styles";
import { Box, Typography, TextField, Button } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import { isIE } from "react-device-detect";

import { useDepartments, Department } from "../../../modules/dictionaries/shared";
import { ComboBoxField } from "../../../components/ComboBoxField";
import messages from "../constants/messages";
import {
  INITIAL_FORM_STATE,
  addUserFormConfig,
  namePatt,
  loginPatt,
  MAX_LOGIN_LENGTH,
  MAX_NAME_LENGTH,
  MAX_SURNAME_LENGTH,
} from "../constants/constants";
import { styles } from "../constants/styles";
import { UserInputFormProps } from "../constants/types";
import { useAdminDepartment, useAdministrationError, AdministrationError } from "../../../modules/admin";
import cn from "classnames";
import { useEffect } from "react";
import { AVAILEBLE_EMAIL_DOMAINS } from "../../../modules/auth/pages/RegisterPage/constants/constants";

type Props = UserInputFormProps & WithStyles<typeof styles>;

const UserInputForm: React.FC<Props> = (props: Props) => {
  const [, , , createUser, updateUser] = useAdminDepartment();
  const { classes, row, variant } = props;
  const [administrationError] = useAdministrationError();
  const [, departmentsWithNamesDeleted] = useDepartments();

  let initialState = INITIAL_FORM_STATE;
  if (row && variant === "edit") {
    const { departmentId, name, isDeleted } = row;
    initialState = {
      ...INITIAL_FORM_STATE,
      department: departmentId,
      requiredFields: {
        name,
        isDeleted,
      },
    };
  }
  if (variant === "add") {
    const department = departmentsWithNamesDeleted.find((item) => item.isDeleted === false);
    initialState = {
      ...INITIAL_FORM_STATE,
      department: department ? department.id : INITIAL_FORM_STATE.department,
    };
  }
  const [formState, setFormState] = React.useState(initialState);

  useEffect(() => {
    if (administrationError === AdministrationError.DuplicateEmail) {
      setFormState({ ...formState, errors: { ...formState.errors }, showErrorMessage: true });
    }
  }, [administrationError]);

  useEffect(() => {
    setFormState({ ...formState, showErrorMessage: false });
  }, [variant]);

  const getErrors = (id: string, value: string) => {
    let errors = {};
    if (id === "login") {
      let loginError = !value;
      if (!loginError) {
        const login = value.split("@")[0] || "";
        loginError = !login.match(loginPatt);
      }
      errors[id] = loginError;

      const domain = value.split("@")[1] || "";
      const domainError = !AVAILEBLE_EMAIL_DOMAINS.some((d) => d === domain);
      errors["domain"] = domainError;
    }
    if (id === "name" || id === "surname") {
      const nameOrSurnameError = !value || !value.match(namePatt);
      errors[id] = nameOrSurnameError;
    }
    if (id === "department") {
      errors[id] = !value;
    }

    return errors;
  };

  const handleChangeText = (id: string) => (event: any) => {
    let errors = {};

    if (formState.showErrorMessage) {
      errors = getErrors(id, event.target.value.trim());
    }

    if (Object.keys(formState.requiredFields).includes(id)) {
      setFormState({
        ...formState,
        requiredFields: { ...formState.requiredFields, [id]: event.target.value.trim() },
        dirty: true,
        errors: {
          ...formState.errors,
          ...errors,
        },
      });
    } else {
      setFormState({
        ...formState,
        [id]: event.target.value,
        dirty: true,
        errors: {
          ...formState.errors,
          ...errors,
        },
      });
    }
  };

  const handleChangeDepartment = (event: React.ChangeEvent<{}>, value: any) => {
    if (value) {
      setFormState({
        ...formState,
        department: value.id,
        errors: { ...formState.errors },
        dirty: true,
      });
    } else {
      setFormState({ ...formState, department: 0, errors: { ...formState.errors  }, dirty: true });
    }
  };

  const handleApply = () => {
    const isValid = checkValidity();

    if (isValid) {
      const { department } = formState;
      const { name, isDeleted } = formState.requiredFields;
      const { handleCancel } = props;

      if (variant === "edit") {
        const { id } = props.row!;
        updateUser({
          id,
          name,
          isDeleted,
        });
        handleCancel();
      } else if (variant === "add") {
        const result = createUser({
          name,
          isDeleted
        });

        result.then((res) => {
          if (res) {
            handleCancel();
          }
        });
      }
    }
  };

  const checkValidity = () => {
    const { name, isDeleted } = formState.requiredFields;
    const nameError = !name.length || name.length > MAX_NAME_LENGTH;
    const errors = [nameError];
    const showErrorMessage = errors.some((error) => error === true);

    setFormState({
      ...formState,
      errors: {
        ...formState.errors,
        name: nameError,
      },
      showErrorMessage,
    });

    return errors.every((error) => error === false);
  };

  const renderHelperText = (id: string) => {
    if (id === "name" || id === "surname") {
      return (
        (!formState.requiredFields[id]?.length && formState.showErrorMessage && (
          <FormattedMessage {...messages.fieldIsRequired} />
        )) ||
        (formState.showErrorMessage && formState.errors[id] && <FormattedMessage {...messages.checkCorrectness} />)
      );
    }

    return undefined;
  };
  const headerTitleMessage = variant === "add" ? messages.addUserFormHeader : messages.editUserFormHeader;

  return (
    <Box className={classes.modalBox}>
      <Typography className={classes.modalHeader}>
        <FormattedMessage {...headerTitleMessage} />
      </Typography>
      {addUserFormConfig.map((field) => {
        return (
          <div className={classes.fieldItem} key={field.id}>
            {field.type === "text" && (
              <TextField
                id={field.id}
                className={classes.fieldContent}
                error={formState.errors[field.id]}
                type={"text"}
                variant="outlined"
                fullWidth
                value={formState.requiredFields[field.id]}
                label={<FormattedMessage {...field.name} />}
                InputLabelProps={{ classes: { root: classes.modalInputLabel }, shrink: isIE ? true : undefined }}
                onChange={handleChangeText(field.id)}
                helperText={renderHelperText(field.id)}
                FormHelperTextProps={{ classes: { root: classes.helperText } }}
                classes={{ root: classes.textField }}
                inputProps={{ maxLength: field.maxLength }}
              ></TextField>
            )}
          </div>
        );
      })}
      <div className={classes.modalButtonBase}>
        <Button
          className={classes.modalButtons}
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={row && variant === "edit" && !formState.dirty}
        >
          <FormattedMessage {...messages.saveButton} />
        </Button>
        <Button
          className={cn(classes.modalButtons, classes.btnCancel)}
          variant="contained"
          onClick={props.handleCancel}
        >
          <FormattedMessage {...messages.cancelButton} />
        </Button>
      </div>
    </Box>
  );
};

export default withStyles(styles)(UserInputForm);
